/**
 * Stores > HQ broadcast at /stores/hq/broadcast.
 *
 * A composer to send a broadcast to All stores, the Pilot 16, or a Custom set,
 * plus a history list of past broadcasts with their target counts. The custom
 * store picker sources its store list from the scorecard endpoint (every active
 * store, with pilot16 flags), which is already loaded HQ-side.
 *
 * Besides the Sweet Spot message every broadcast creates, the sender chooses
 * per broadcast whether it also pops a message box on each store's back-office
 * PC and whether it also emails each store. Both default on.
 *
 * Each history card shows those two choices and the delivery summary the API
 * composed when the broadcast was sent, so what happened outlives the composer.
 * A broadcast sent before those columns existed has them as null, and its
 * indicators are omitted rather than drawn as off.
 *
 * Gated by stores:hq. 404/503 -> not-available placeholder.
 */

import { useMemo, useState } from 'react';
import { Send, Search, Check, X } from 'lucide-react';
import { Pill } from '@/components/Pill';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { useSession } from '@/lib/useSession';
import { hasAccessSlug } from '@/lib/toolVisibility';
import { formatDateTime } from '@/lib/dateUtils';
import {
  useBroadcasts,
  useCreateBroadcast,
  useScorecard,
  isNotAvailable,
  denverToday,
  type BroadcastAudience,
  type Broadcast,
} from '@/lib/useStores';
import {
  StoresHeader,
  AccessDeniedCard,
  NotAvailableCard,
  ErrorCard,
  primaryBtnCls,
  inputCls,
  labelCls,
} from './storesShared';

const AUDIENCE_LABELS: Record<BroadcastAudience, string> = {
  all: 'All stores',
  pilot: 'Pilot 16',
  custom: 'Custom',
};

export function HqBroadcast() {
  const { user } = useSession();
  const canView = hasAccessSlug(user?.permissions, 'stores.broadcast', user?.superAdmin);
  const bcQ = useBroadcasts();

  if (user && !canView) return <AccessDeniedCard what="Broadcasts" />;
  if (isNotAvailable(bcQ.error)) {
    return (
      <div>
        <StoresHeader title="Broadcast" actions={<ToolPinToggle toolSlug="stores-hq-broadcast" toolName="Broadcast" />} />
        <NotAvailableCard what="Broadcasts" />
      </div>
    );
  }

  return (
    <div className="mx-auto grid w-full max-w-5xl gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <div>
        <StoresHeader
          title="Broadcast"
          subtitle="Send one message to every store or a selected group."
          actions={<ToolPinToggle toolSlug="stores-hq-broadcast" toolName="Broadcast" />}
        />
        <Composer />
      </div>

      <div className="lg:pt-1">
        <h2 className="mb-3 text-[16px] font-semibold text-amp-ink dark:text-amp-dark-ink">Recent broadcasts</h2>
        {bcQ.isLoading ? (
          <div className="space-y-3">
            {[0, 1].map((i) => (
              <div key={i} className="h-20 animate-pulse rounded-lg bg-amp-cream dark:bg-amp-dark-surface-warm" />
            ))}
          </div>
        ) : bcQ.error ? (
          <ErrorCard message={bcQ.error.message} />
        ) : bcQ.broadcasts.length === 0 ? (
          <div className="rounded-lg border border-dashed border-amp-line px-4 py-8 text-center dark:border-amp-dark-line">
            <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">No broadcasts sent yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {bcQ.broadcasts.map((b) => (
              <BroadcastCard key={b.id} b={b} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function BroadcastCard({ b }: { b: Broadcast }) {
  return (
    <div className="rounded-lg border border-amp-line bg-amp-white p-4 dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[15px] font-semibold text-amp-ink dark:text-amp-dark-ink">{b.title}</span>
        <Pill tone="bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300">
          {AUDIENCE_LABELS[b.audience]}
        </Pill>
        <span className="text-[12px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
          {b.target_count} {b.target_count === 1 ? 'store' : 'stores'}
        </span>
        {b.created_at && (
          <span className="ml-auto text-[12px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
            {formatDateTime(b.created_at)}
          </span>
        )}
      </div>
      <p className="mt-1.5 whitespace-pre-wrap text-[14px] text-amp-ink dark:text-amp-dark-ink">{b.body}</p>
      {/*
        What the sender asked for. Omitted entirely when the API sends null, which
        means the broadcast predates the columns and the choice was never recorded:
        an older broadcast must not look like one where both channels were switched
        off. The two are independent, so each is shown only when it is known.
      */}
      {(typeof b.notify_boh_popup === 'boolean' || typeof b.notify_store_email === 'boolean') && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {typeof b.notify_boh_popup === 'boolean' && (
            <ChannelPill label="Store computer" on={b.notify_boh_popup} />
          )}
          {typeof b.notify_store_email === 'boolean' && (
            <ChannelPill label="Email" on={b.notify_store_email} />
          )}
        </div>
      )}
      {b.delivery_summary && (
        <p className="mt-1.5 text-[12px] text-amp-ink-muted dark:text-amp-dark-ink-muted">{b.delivery_summary}</p>
      )}
      {b.created_by && (
        <p className="mt-1 text-[12px] text-amp-ink-muted dark:text-amp-dark-ink-muted">Sent by {b.created_by}</p>
      )}
    </div>
  );
}

/**
 * One channel the sender either asked for or left off, in plain words: a filled
 * green pill with a tick when it was on, an outline pill with a cross when it was
 * off. Same shape as the phase pills on the HQ scorecard.
 *
 * Only rendered when the answer is known. An unknown channel gets no pill at all.
 */
function ChannelPill({ label, on }: { label: string; on: boolean }) {
  return on ? (
    <span className="inline-flex items-center gap-1 rounded-pill bg-green-100 px-2.5 py-1 text-[12px] font-semibold text-green-800 dark:bg-green-900/40 dark:text-green-300">
      <Check className="h-3 w-3" aria-hidden /> {label} on
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-pill border border-amp-line px-2.5 py-1 text-[12px] font-semibold text-amp-ink-muted dark:border-amp-dark-line dark:text-amp-dark-ink-muted">
      <X className="h-3 w-3" aria-hidden /> {label} off
    </span>
  );
}

function Composer() {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState<BroadcastAudience>('all');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [notifyPopup, setNotifyPopup] = useState(true);
  const [notifyEmail, setNotifyEmail] = useState(true);
  const create = useCreateBroadcast();

  // Store list for the custom picker: reuse the scorecard (all active stores).
  // Only fetched when the custom audience is chosen.
  const scoreQ = useScorecard(denverToday());
  const [pickerQuery, setPickerQuery] = useState('');
  const stores = useMemo(() => {
    const list = scoreQ.data?.stores ?? [];
    const term = pickerQuery.trim().toLowerCase();
    const filtered = term
      ? list.filter((s) => s.store_name.toLowerCase().includes(term) || String(s.store_no).includes(term))
      : list;
    return [...filtered].sort((a, b) => a.store_name.localeCompare(b.store_name));
  }, [scoreQ.data, pickerQuery]);

  const toggle = (storeNo: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(storeNo)) next.delete(storeNo);
      else next.add(storeNo);
      return next;
    });
  };

  const canSend =
    title.trim().length > 0 &&
    body.trim().length > 0 &&
    (audience !== 'custom' || selected.size > 0) &&
    !create.isPending;

  const [sentMsg, setSentMsg] = useState<string | null>(null);

  const send = () => {
    if (!canSend) return;
    create.mutate(
      {
        title: title.trim(),
        body: body.trim(),
        audience,
        store_nos: audience === 'custom' ? Array.from(selected) : undefined,
        notify_boh_popup: notifyPopup,
        notify_store_email: notifyEmail,
      },
      {
        onSuccess: (data) => {
          // The API composes this sentence and stores it on the broadcast, so the
          // line here and the one on the history card are the same string rather
          // than two builders that can drift. An API that predates the field falls
          // back to the plain count.
          const count = data.broadcast.target_count;
          setSentMsg(
            data.broadcast.delivery_summary ??
              `Sent to ${count} ${count === 1 ? 'store' : 'stores'}.`,
          );
          setTitle('');
          setBody('');
          setSelected(new Set());
          setAudience('all');
          setNotifyPopup(true);
          setNotifyEmail(true);
          // Deliberately no timer here. This line used to clear itself after five
          // seconds and Steven kept losing the delivery summary before he could
          // read it. It stays until the next successful send overwrites it, or
          // until the page is left or reloaded, which unmounts the component. It is
          // never blanked: a send failure shows in its own line below instead.
        },
      },
    );
  };

  return (
    <div className="rounded-lg border border-amp-line bg-amp-white p-4 dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <div className="mb-3">
        <label htmlFor="bc-title" className={labelCls}>Title</label>
        <input
          id="bc-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Short headline"
          className={`${inputCls} py-2.5 text-[15px] sm:py-1.5 sm:text-sm`}
        />
      </div>

      <div className="mb-3">
        <label htmlFor="bc-body" className={labelCls}>Message</label>
        {/* STAYS A PLAIN BOX, deliberately. This message is emailed to every
            store, and the mailer sends plain text only, so storing HTML here
            would put raw markup in store inboxes. Widening the mailer is
            tracked separately and is not approved. Do not convert this to the
            rich-text editor until that lands. */}
        <textarea
          id="bc-body"
          rows={5}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="What do you want every store to know?"
          className={`${inputCls} py-2.5 text-[15px] sm:py-1.5 sm:text-sm`}
        />
      </div>

      <div className="mb-3">
        <p className={labelCls}>Audience</p>
        <div className="flex flex-wrap gap-2">
          {(['all', 'pilot', 'custom'] as BroadcastAudience[]).map((a) => (
            <Pill key={a} variant={audience === a ? 'selected' : 'outline'} onClick={() => setAudience(a)}>
              {AUDIENCE_LABELS[a]}
            </Pill>
          ))}
        </div>
      </div>

      {audience === 'custom' && (
        <div className="mb-3 rounded-lg border border-amp-line p-3 dark:border-amp-dark-line">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[13px] font-medium text-amp-ink dark:text-amp-dark-ink">
              {selected.size} selected
            </span>
            {selected.size > 0 && (
              <button
                type="button"
                onClick={() => setSelected(new Set())}
                className="text-xs text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
              >
                Clear
              </button>
            )}
          </div>
          <div className="relative mb-2">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-amp-ink-muted" aria-hidden />
            <input
              value={pickerQuery}
              onChange={(e) => setPickerQuery(e.target.value)}
              placeholder="Search stores..."
              className={`${inputCls} py-2.5 pl-8 text-[15px] sm:py-1.5 sm:text-sm`}
            />
          </div>
          {scoreQ.isLoading ? (
            <p className="py-3 text-center text-[13px] text-amp-ink-muted dark:text-amp-dark-ink-muted">Loading stores...</p>
          ) : isNotAvailable(scoreQ.error) || scoreQ.error ? (
            <p className="py-3 text-center text-[13px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
              Store list is not available right now.
            </p>
          ) : (
            <div className="max-h-64 space-y-0.5 overflow-auto sm:max-h-56 sm:space-y-1">
              {stores.map((s) => (
                <label
                  key={s.store_no}
                  className="flex min-h-[44px] cursor-pointer items-center gap-2.5 rounded px-2 py-2 hover:bg-amp-cream-warm sm:min-h-0 sm:py-1.5 dark:hover:bg-amp-dark-surface-warm"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(s.store_no)}
                    onChange={() => toggle(s.store_no)}
                    className="h-5 w-5 shrink-0 rounded border-amp-line accent-amp-olive sm:h-4 sm:w-4"
                  />
                  <span className="text-[15px] text-amp-ink sm:text-[14px] dark:text-amp-dark-ink">{s.store_name}</span>
                  {s.pilot16 && (
                    <Pill tone="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">Pilot 16</Pill>
                  )}
                </label>
              ))}
              {stores.length === 0 && (
                <p className="py-3 text-center text-[13px] text-amp-ink-muted dark:text-amp-dark-ink-muted">No stores match.</p>
              )}
            </div>
          )}
        </div>
      )}

      <div className="mb-3">
        <p className={labelCls}>Also send</p>
        <div className="space-y-0.5 sm:space-y-1">
          <label className="flex min-h-[44px] cursor-pointer select-none items-center gap-2.5 sm:min-h-0">
            <input
              type="checkbox"
              checked={notifyPopup}
              onChange={(e) => setNotifyPopup(e.target.checked)}
              className="h-5 w-5 shrink-0 rounded border-amp-line accent-amp-olive sm:h-4 sm:w-4"
            />
            <span className="text-[15px] text-amp-ink sm:text-[14px] dark:text-amp-dark-ink">
              Pop up a message on the store computer
            </span>
          </label>
          <label className="flex min-h-[44px] cursor-pointer select-none items-center gap-2.5 sm:min-h-0">
            <input
              type="checkbox"
              checked={notifyEmail}
              onChange={(e) => setNotifyEmail(e.target.checked)}
              className="h-5 w-5 shrink-0 rounded border-amp-line accent-amp-olive sm:h-4 sm:w-4"
            />
            <span className="text-[15px] text-amp-ink sm:text-[14px] dark:text-amp-dark-ink">
              Email the store
            </span>
          </label>
        </div>
        <p className="mt-1 text-[12px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
          The pop-up only appears when someone is signed in at the store computer, so email is the
          one that always arrives.
        </p>
      </div>

      <button
        type="button"
        onClick={send}
        disabled={!canSend}
        className={`${primaryBtnCls} min-h-[44px] w-full justify-center py-2.5 sm:min-h-0 sm:py-2`}
      >
        <Send className="h-3.5 w-3.5" /> {create.isPending ? 'Sending...' : 'Send broadcast'}
      </button>

      {sentMsg && <p className="mt-2 text-center text-[13px] text-green-700 dark:text-green-300">{sentMsg}</p>}
      {create.error && (
        <p className="mt-2 text-center text-[13px] text-red-700 dark:text-red-300">Could not send: {create.error.message}</p>
      )}
    </div>
  );
}
